import React from "react";
import { Loader2, RefreshCw, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";

const DOT = {
  green: "bg-emerald-500",
  yellow: "bg-amber-500",
  red: "bg-red-600",
  gray: "bg-gray-400",
};

function Pill({ color, label, sub }) {
  return (
    <div className="bg-white border border-brand p-4 flex items-start gap-3" data-testid={`health-pill-${label?.toLowerCase()?.replace(/\s+/g, "-") || "x"}`}>
      <span className={`inline-block w-3 h-3 rounded-full mt-1 flex-shrink-0 ${DOT[color] || "bg-gray-400"}`}></span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-brand-dark">{label}</div>
        {sub && <div className="text-xs text-brand-text2 mt-0.5 break-words">{sub}</div>}
      </div>
    </div>
  );
}

function CounterCard({ label, value, tone }) {
  const toneCls = tone === "warn" ? "text-amber-700" : tone === "bad" ? "text-red-700" : "text-brand-dark";
  return (
    <div className="bg-white border border-brand p-4" data-testid={`health-counter-${label?.toLowerCase()?.replace(/\s+/g, "-")}`}>
      <div className={`font-display font-bold text-3xl ${toneCls}`}>{value ?? 0}</div>
      <div className="text-xs text-brand-text2 mt-1">{label}</div>
    </div>
  );
}

export default function HealthCheckWidget() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true); setError("");
    try {
      const res = await adminApi().get("/admin/health-check");
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Kunne ikke hente status");
      toast.error("Health-check fejlede");
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); const t = setInterval(load, 60_000); return () => clearInterval(t); }, [load]);

  if (loading && !data) return <div className="text-brand-text2 flex items-center gap-2"><Loader2 className="animate-spin" size={16}/> Indlæser system-status...</div>;
  if (error && !data) return <div className="text-red-600">{error}</div>;
  if (!data) return null;

  const b = data.b2b || {};
  const ds = data.drivers_subcontractors || {};
  const overallOk = data.overall?.ok;

  return (
    <div className="space-y-5" data-testid="health-check-widget">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="font-display font-bold text-2xl text-brand-dark">System-status</h3>
        <span className={`text-xs px-2 py-1 ${overallOk ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`} data-testid="health-overall-badge">
          {overallOk ? "Alt OK" : `${data.overall?.critical_issues?.length || 0} kritiske problemer`}
        </span>
        <span className="text-xs text-brand-text2 ml-auto">Sidst tjekket: {new Date(data.checked_at_iso).toLocaleTimeString("da-DK")}</span>
        <button onClick={load} className="text-xs px-2 py-1 border border-brand inline-flex items-center gap-1" data-testid="health-refresh">
          <RefreshCw size={12}/> Opdatér
        </button>
      </div>

      {!overallOk && data.overall?.critical_issues?.length > 0 && (
        <div className="bg-red-50 border border-red-200 p-3 text-sm flex items-start gap-2" data-testid="health-critical-issues">
          <AlertTriangle className="text-red-600 mt-0.5 flex-shrink-0" size={18}/>
          <div>
            <div className="font-semibold text-red-800">Kritiske problemer:</div>
            <ul className="list-disc list-inside text-red-700 mt-1">
              {data.overall.critical_issues.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>
        </div>
      )}

      {/* Integrations row */}
      <div>
        <h4 className="font-semibold text-brand-dark mb-2">Integrationer</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Pill color={data.openrouteservice?.color} label="OpenRouteService" sub={data.openrouteservice?.label}/>
          <Pill color={data.cvrapi?.color} label="cvrapi.dk" sub={data.cvrapi?.label}/>
          <Pill color={data.stripe?.color} label="Stripe" sub={data.stripe?.label}/>
          <Pill color={data.hub?.color} label="HUB" sub={data.hub?.label}/>
        </div>
      </div>

      {/* B2B counters */}
      <div>
        <h4 className="font-semibold text-brand-dark mb-2">B2B drift</h4>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <CounterCard label="Aktive aftaler" value={b.active_agreements}/>
          <CounterCard label="Pakker til afhentning" value={b.parcels_waiting_pickup} tone={b.parcels_waiting_pickup > 50 ? "warn" : ""}/>
          <CounterCard label="Pakker på HUB" value={b.parcels_at_hub}/>
          <CounterCard label="Ruter til godkendelse" value={b.routes_needing_review} tone={b.routes_needing_review > 0 ? "warn" : ""}/>
          <CounterCard label="Røde/kritiske ruter i dag" value={b.routes_red_or_critical_today} tone={b.routes_red_or_critical_today > 0 ? "bad" : ""}/>
        </div>
      </div>

      {/* Drivers */}
      <div>
        <h4 className="font-semibold text-brand-dark mb-2">Chauffører og vognmænd</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <CounterCard label="Aktive chauffører" value={ds.active_drivers}/>
          <CounterCard label="Vognmænd i alt" value={ds.total_subcontractors}/>
        </div>
      </div>

      {overallOk && (
        <div className="bg-emerald-50 border border-emerald-200 p-3 text-sm flex items-center gap-2" data-testid="health-ok">
          <CheckCircle2 className="text-emerald-600" size={18}/>
          <span className="text-emerald-800">Ingen kritiske problemer — systemet er klar til drift.</span>
        </div>
      )}

      <DropTestDataPanel/>
    </div>
  );
}

function DropTestDataPanel() {
  const [preview, setPreview] = React.useState(null);
  const [confirm, setConfirm] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  const loadPreview = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi().post("/admin/tools/drop-test-data/preview");
      setPreview(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Preview fejlede"); }
    finally { setBusy(false); }
  };

  const execute = async () => {
    if (confirm !== "DELETE TEST DATA") { toast.error("Skriv 'DELETE TEST DATA' præcis"); return; }
    if (!window.confirm("Sidste advarsel — slet alle test-data nu?")) return;
    setBusy(true);
    try {
      const { data } = await adminApi().post("/admin/tools/drop-test-data", { confirm, scopes: ["b2b_test_data", "preview_customers", "tracking_test", "demo_bookings_extra"] });
      toast.success(`Slettede ${data.total_deleted || 0} dokumenter`);
      setConfirm("");
      setPreview(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fejl"); }
    finally { setBusy(false); }
  };

  return (
    <details className="bg-red-50 border-2 border-red-200 p-4" data-testid="drop-test-data-panel">
      <summary className="cursor-pointer font-display font-bold text-lg text-red-800">
        ⚠️ Drop test-data (preview & sandbox cleanup)
      </summary>
      <div className="mt-4 space-y-3 text-sm">
        <p className="text-red-900">
          Dette værktøj sletter <strong>kun test/demo/sandbox data</strong> baseret på strenge markører
          (navne/emails der indeholder &quot;test&quot;, &quot;demo&quot;, &quot;sandbox&quot;, &quot;e2e&quot;, &quot;example.dk&quot;).
          Rigtige kunder, forsendelser, fakturaer og review-konti røres ALDRIG. Handlingen logges i audit-log.
        </p>
        <button onClick={loadPreview} disabled={busy} className="btn-secondary" data-testid="drop-test-data-preview-btn">
          {busy ? <Loader2 className="animate-spin" size={14}/> : "Vis preview af hvad der vil blive slettet"}
        </button>
        {preview && (
          <pre className="bg-white border border-red-200 p-3 text-xs overflow-x-auto" data-testid="drop-test-data-preview">{JSON.stringify(preview.scopes, null, 2)}</pre>
        )}
        {preview && (preview.total_would_delete > 0) && (
          <div className="bg-white border border-red-300 p-3 space-y-2">
            <div className="text-red-900 font-semibold">Skriv &quot;DELETE TEST DATA&quot; for at bekræfte ({preview.total_would_delete} dokumenter):</div>
            <input className="input-flat" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE TEST DATA" data-testid="drop-test-data-confirm"/>
            <button onClick={execute} disabled={busy || confirm !== "DELETE TEST DATA"} className="bg-red-600 text-white px-4 py-2 hover:bg-red-700 disabled:opacity-50" data-testid="drop-test-data-execute">
              {busy ? <Loader2 className="animate-spin" size={14}/> : "🗑️ Slet test-data NU"}
            </button>
          </div>
        )}
      </div>
    </details>
  );
}
